import { useCallback, useMemo } from 'react';
import getUniqueSelector from '../utils/selector';

// ==========================================
// TYPES & INTERFACES
// ==========================================

export interface ViewportAnalysis {
  head: PageMetadata;
  viewport: ViewportInfo;
  sections: Record<string, SerializedElement | null>;
  elementCount: number;
  analysisTime: string;
  error?: string;
  analysisScope?: 'viewport' | 'fullDocument';
}

export interface SerializedElement {
  id: string;
  tag: string;
  text: string | null;
  path: string;
  parentId: string | null;
  role: string | null;
  aria: Record<string, string>;
  href: string | null;
  interactivity: InteractivityInfo;
  headingLevel: number | null;
  position: ElementPosition;
  className: string | null;
  children: SerializedElement[];
}

export interface InteractivityInfo {
  isInteractive: boolean;
  clickable: boolean;
  focusable: boolean;
  hasEventListeners: boolean;
  interactionType: string;
  eventAttributes: string[];
}

export interface ElementPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface PageMetadata {
  title: string;
  url: string;
  meta: Array<{ name: string; content: string }>;
  canonical: string | null;
}

export interface ViewportInfo {
  width: number;
  height: number;
  scrollX: number;
  scrollY: number;
  devicePixelRatio: number;
  orientation: string;
  userAgent: string;
  documentWidth: number;
  documentHeight: number;
  visibleArea: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  timestamp: string;
}

// AtariMates specific export interfaces
export interface AtariMatesAnalysis {
  page_metadata: {
    title: string;
    url: string;
    description?: string;
    viewport: {
      width: number;
      height: number;
      device_type: 'mobile' | 'tablet' | 'desktop';
    };
    timestamp: string;
    analysis_scope?: 'viewport' | 'fullDocument';
  };
  elements: AtariMatesElement[];
  issues_detected: AtariMatesIssue[];
  positive_notes: AtariMatesPositiveNote[];
}

export interface AtariMatesElement {
  id: string;
  path: string;
  tag: string;
  text: string | null;
  href: string | null;
  position: ElementPosition;
  interactivity: InteractivityInfo;
  aria: Record<string, string>;
  className: string | null;
  role: string | null;
  headingLevel: number | null;
  context: string;
  visible: boolean;
  issues: string[];
}

export interface AtariMatesIssue {
  expert: 'Pixel' | 'Navi' | 'Index' | 'Lexi' | 'Glitch';
  badge: string;
  element: {
    id: string;
    path: string;
  };
  issueType: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: string;
}

export interface AtariMatesPositiveNote {
  expert: 'Pixel' | 'Navi' | 'Index' | 'Lexi' | 'Glitch';
  element: {
    id: string;
    path: string;
  };
  description: string;
}

// ==========================================
// VIEWPORT ANALYZER HOOK
// ==========================================

const useViewportAnalyzer = () => {
  const eventAttributes = useMemo(() => [
    'onclick', 'onmousedown', 'onmouseup', 'onkeydown',
    'onkeyup', 'onsubmit', 'onchange', 'oninput', 'onfocus', 'onblur'
  ], []);

  // Get only direct text content
  const getOwnText = useCallback((el: Element): string | null => {
    const textNodes = Array.from(el.childNodes)
      .filter((n): n is Text => n.nodeType === Node.TEXT_NODE)
      .map(n => n.textContent?.trim() || '')
      .filter(Boolean);

    const text = textNodes.join(' ').slice(0, 100);
    return text || null;
  }, []);

  // Enhanced interactivity detection
  const getInteractivityInfo = useCallback((el: Element): InteractivityInfo => {
    const tag = el.tagName.toLowerCase();
    const role = el.getAttribute('role');
    const type = el.getAttribute('type');
    const computedStyle = window.getComputedStyle(el);

    // Check for event attributes
    const foundEventAttributes = eventAttributes.filter(attr =>
      el.hasAttribute(attr)
    );

    // Detect interactive elements
    const isButton = tag === 'button' || role === 'button';
    const isLink = tag === 'a' && el.hasAttribute('href');
    const isInput = ['input', 'select', 'textarea'].includes(tag);
    const hasInteractiveRole = ['button', 'link', 'tab', 'menuitem', 'option'].includes(role || '');
    const hasEventHandlers = foundEventAttributes.length > 0;
    const hasClickableClass = ['btn', 'button', 'clickable'].some(cls =>
      el.classList.contains(cls)
    );
    const hasPointerCursor = computedStyle.cursor === 'pointer';
    const isFocusable = (el as HTMLElement).tabIndex >= 0 || (el as HTMLElement).isContentEditable;

    const isInteractive = isButton || isLink || isInput || hasInteractiveRole ||
      hasEventHandlers || hasClickableClass || hasPointerCursor;

    // Detect React event listeners (heuristic)
    const hasEventListeners = (() => {
      try {
        // @ts-expect-error - this may be a React element
        const reactFiber = (el as unknown)._reactInternalFiber;

        if (reactFiber?.memoizedProps) {
          const props = reactFiber.memoizedProps;
          return Object.keys(props).some(key =>
            key.startsWith('on') && typeof props[key] === 'function'
          );
        }

        return hasEventHandlers;
      } catch {
        return hasEventHandlers;
      }
    })();

    // Determine interaction type
    const getInteractionType = (): string => {
      if (isLink) return 'link';
      if (isButton) return 'button';
      if (tag === 'input') return `input-${type || 'text'}`;
      if (tag === 'select') return 'select';
      if (tag === 'textarea') return 'textarea';
      if (role === 'tab') return 'tab';
      if (role === 'menuitem') return 'menuitem';
      if (hasClickableClass || hasPointerCursor) return 'clickable';
      return 'unknown';
    };

    return {
      isInteractive,
      clickable: isInteractive,
      focusable: isFocusable,
      hasEventListeners,
      interactionType: getInteractionType(),
      eventAttributes: foundEventAttributes
    };
  }, [eventAttributes]);

  // Check if element is visible in viewport or full document
  const isElementVisible = useCallback((el: Element, viewportHeight: number, analyzeFullDocument: boolean = false): boolean => {
    const rect = el.getBoundingClientRect();
    const style = window.getComputedStyle(el);

    // Basic visibility checks
    const isVisible = (
      rect.width > 0 &&
      rect.height > 0 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      parseFloat(style.opacity) > 0
    );

    if (!isVisible) return false;

    // If analyzing full document, don't filter by viewport position
    if (analyzeFullDocument) {
      return true;
    }

    // For viewport-only analysis, check if element is in viewport
    return rect.bottom > 0 && rect.top < viewportHeight;
  }, []);

  // Serialize DOM element and its children
  const serializeElement = useCallback((
    el: Element,
    depth = 0,
    parentId: string | null = null,
    viewportHeight: number,
    maxDepth = 30,
    analyzeFullDocument: boolean = false
  ): SerializedElement | null => {
    if (!el || el.nodeType !== Node.ELEMENT_NODE || depth > maxDepth) {
      return null;
    }

    if (!isElementVisible(el, viewportHeight, analyzeFullDocument)) {
      return null;
    }

    const nodeId = `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const tag = el.tagName.toLowerCase();
    const rect = el.getBoundingClientRect();

    // Get relevant attributes efficiently
    const attributes: Record<string, string> = {};
    const relevantAttrs = ['id', 'class', 'href', 'src', 'role'];

    relevantAttrs.forEach(attr => {
      const value = el.getAttribute(attr);
      if (value) attributes[attr] = value;
    });

    // Get ARIA attributes
    const aria: Record<string, string> = {};
    Array.from(el.attributes).forEach(attr => {
      if (attr.name.startsWith('aria-')) {
        aria[attr.name] = attr.value;
      }
    });

    // Process children (with early termination for performance)
    const children: SerializedElement[] = [];
    const childElements = Array.from(el.children);

    for (const child of childElements) {
      if (children.length >= 50) break;

      const serializedChild = serializeElement(child, depth + 1, nodeId, viewportHeight, maxDepth, analyzeFullDocument);
      if (serializedChild) {
        children.push(serializedChild);
      }
    }

    // Convert relative URLs to absolute
    const hrefValue = attributes.href
      ? new URL(attributes.href, window.location.href).href
      : null;

    return {
      id: nodeId,
      tag,
      text: getOwnText(el),
      path: getUniqueSelector(el) || '',
      parentId,
      role: el.getAttribute('role') || null,
      aria,
      href: hrefValue,
      interactivity: getInteractivityInfo(el),
      headingLevel: /^h[1-6]$/.test(tag) ? parseInt(tag[1], 10) : null,
      position: {
        top: Math.round(rect.top),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
        height: Math.round(rect.height)
      },
      className: attributes.class || null,
      children
    };
  }, [isElementVisible, getOwnText, getInteractivityInfo]);

  // Count total elements in analysis
  const countElements = useCallback((sections: Record<string, SerializedElement | null>): number => {
    let count = 0;

    const countInSection = (section: SerializedElement | null): void => {
      if (!section) return;
      count++;
      section.children.forEach(countInSection);
    };

    Object.values(sections).forEach(countInSection);
    return count;
  }, []);

  // Main analyzer function with optional full document analysis
  const analyzeViewport = useCallback((options: { analyzeFullDocument?: boolean } = {}): ViewportAnalysis => {
    const { analyzeFullDocument = false } = options;

    try {
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Get page metadata
      const head: PageMetadata = {
        title: document.title,
        url: window.location.href,
        meta: Array.from(document.head.querySelectorAll('meta'))
          .map(m => ({
            name: m.getAttribute('name') || m.getAttribute('property') || '',
            content: m.getAttribute('content') || ''
          }))
          .filter(m => m.name),
        canonical: document.querySelector("link[rel='canonical']")?.getAttribute('href') || null
      };

      // Find semantic sections
      const sections: Record<string, SerializedElement | null> = {};
      const sectionRoles = ['main', 'header', 'footer', 'nav', 'aside'];

      sectionRoles.forEach(role => {
        const el = document.querySelector(`[role='${role}']`) ||
          document.querySelector(role) ||
          document.getElementsByTagName(role)[0];

        if (el && isElementVisible(el, viewportHeight, analyzeFullDocument)) {
          sections[role] = serializeElement(el, 0, null, viewportHeight, 30, analyzeFullDocument);
        }
      });

      // If no main section found, analyze the body
      if (!sections.main && document.body) {
        const bodySection = analyzeFullDocument ? 'fullDocument' : 'viewport';
        sections[bodySection] = serializeElement(document.body, 0, null, viewportHeight, 30, analyzeFullDocument);
      }

      // Get comprehensive viewport info
      const viewport: ViewportInfo = {
        width: viewportWidth,
        height: viewportHeight,
        scrollX: Math.round(window.scrollX),
        scrollY: Math.round(window.scrollY),
        devicePixelRatio: window.devicePixelRatio || 1,
        orientation: (screen.orientation?.type) || 'unknown',
        userAgent: navigator.userAgent,
        documentWidth: document.documentElement.scrollWidth,
        documentHeight: document.documentElement.scrollHeight,
        visibleArea: {
          top: window.scrollY,
          bottom: window.scrollY + viewportHeight,
          left: window.scrollX,
          right: window.scrollX + viewportWidth
        },
        timestamp: new Date().toISOString()
      };

      return {
        head,
        viewport,
        sections,
        elementCount: countElements(sections),
        analysisTime: new Date().toISOString(),
        analysisScope: analyzeFullDocument ? 'fullDocument' : 'viewport'
      };

    } catch (error) {
      console.error('Analysis failed:', error);
      return {
        head: { title: '', url: '', meta: [], canonical: null },
        viewport: {} as ViewportInfo,
        sections: {},
        elementCount: 0,
        analysisTime: new Date().toISOString(),
        analysisScope: analyzeFullDocument ? 'fullDocument' : 'viewport',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }, [isElementVisible, serializeElement, countElements]);

  return { analyzeViewport };
};

// ==========================================
// ATARIMATES CONVERTER
// ==========================================

export const convertToAtariMatesFormat = (viewportData: ViewportAnalysis): AtariMatesAnalysis => {
  const { head, viewport, sections, analysisScope } = viewportData;

  // Device type detection
  const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
    if (viewport.width <= 768) return 'mobile';
    if (viewport.width <= 1024) return 'tablet';
    return 'desktop';
  };

  // Flatten all elements
  const allElements: AtariMatesElement[] = [];
  const issues: AtariMatesIssue[] = [];
  const positiveNotes: AtariMatesPositiveNote[] = [];

  const processElement = (element: SerializedElement, context: string = '') => {
    const elementContext = context + (element.text ? ` ${element.text}` : '');

    const atariElement: AtariMatesElement = {
      id: element.id,
      path: element.path,
      tag: element.tag,
      text: element.text,
      href: element.href,
      position: element.position,
      interactivity: element.interactivity,
      aria: element.aria,
      className: element.className,
      role: element.role,
      headingLevel: element.headingLevel,
      context: elementContext.trim(),
      visible: true,
      issues: []
    };

    allElements.push(atariElement);

    // PIXEL ISSUES - Design & UI Consistency
    if (element.tag === 'button' || (element.tag === 'a' && element.interactivity.clickable)) {
      // Check button size
      const area = element.position.width * element.position.height;
      if (area < 44 * 44 && getDeviceType() === 'mobile') {
        issues.push({
          expert: 'Pixel',
          badge: 'Touch Target',
          element: { id: element.id, path: element.path },
          issueType: 'Small Touch Target',
          description: `This ${element.tag} is ${element.position.width}x${element.position.height}px - should be at least 44x44px for mobile users`,
          severity: 'medium',
          category: 'usability'
        });
      }
    }

    // NAVI ISSUES - UX & Accessibility
    if (element.interactivity.isInteractive && !element.text && !element.aria['aria-label']) {
      issues.push({
        expert: 'Navi',
        badge: 'Accessibility',
        element: { id: element.id, path: element.path },
        issueType: 'Missing Label',
        description: `This ${element.tag} has no accessible name - add text or aria-label for screen readers`,
        severity: 'high',
        category: 'accessibility'
      });
    }

    // Tab order issues
    if (element.interactivity.focusable && element.position.top < viewport.height) {
      // This would need more complex tab order analysis
    }

    // INDEX ISSUES - SEO & Performance
    if (element.tag === 'img' && !element.aria['aria-label'] && !element.text) {
      issues.push({
        expert: 'Index',
        badge: 'SEO',
        element: { id: element.id, path: element.path },
        issueType: 'Missing Alt Text',
        description: 'Image missing alt text - this hurts SEO and accessibility',
        severity: 'medium',
        category: 'seo'
      });
    }

    // Large images that could affect performance
    const imageArea = element.position.width * element.position.height;
    if (element.tag === 'img' && imageArea > 500000) {
      issues.push({
        expert: 'Index',
        badge: 'Performance',
        element: { id: element.id, path: element.path },
        issueType: 'Large Image',
        description: `Large image (${element.position.width}x${element.position.height}px) - consider optimization`,
        severity: 'low',
        category: 'performance'
      });
    }

    // LEXI ISSUES - Content & Readability
    if (element.text === 'Learn more' || element.text === 'Click here' || element.text === 'Read more') {
      issues.push({
        expert: 'Lexi',
        badge: 'Content',
        element: { id: element.id, path: element.path },
        issueType: 'Vague CTA',
        description: `"${element.text}" doesn't tell users what they'll get - try something more specific`,
        severity: 'medium',
        category: 'content'
      });
    }

    // Long text without breaks
    if (element.text && element.text.length > 200 && !element.text.includes('.') && !element.text.includes('!')) {
      issues.push({
        expert: 'Lexi',
        badge: 'Readability',
        element: { id: element.id, path: element.path },
        issueType: 'Long Text Block',
        description: 'This text block is quite long - consider breaking it up for better readability',
        severity: 'low',
        category: 'readability'
      });
    }

    // GLITCH ISSUES - Functionality & Broken Links
    if (element.href === '#' || element.href === '') {
      issues.push({
        expert: 'Glitch',
        badge: 'Bug',
        element: { id: element.id, path: element.path },
        issueType: 'Broken Link',
        description: 'This link goes nowhere (href="#" or empty) - users will get frustrated',
        severity: 'high',
        category: 'functionality'
      });
    }

    // Missing href on links
    if (element.tag === 'a' && !element.href) {
      issues.push({
        expert: 'Glitch',
        badge: 'Bug',
        element: { id: element.id, path: element.path },
        issueType: 'Invalid Link',
        description: 'This link tag has no href attribute - it won\'t work',
        severity: 'high',
        category: 'functionality'
      });
    }

    // Process children
    element.children.forEach(child => processElement(child, elementContext));
  };

  // Process all sections
  Object.values(sections).forEach(section => {
    if (section) processElement(section);
  });

  // Add positive notes (at least one)
  if (issues.length === 0 || Math.random() > 0.7) {
    // Find a good element to praise
    const goodButton = allElements.find(el =>
      el.tag === 'button' &&
      el.text &&
      el.text !== 'Learn more' &&
      el.position.width * el.position.height >= 44 * 44
    );

    if (goodButton) {
      positiveNotes.push({
        expert: 'Pixel',
        element: { id: goodButton.id, path: goodButton.path },
        description: `Nice work on this button - good size and clear labeling! 👍`
      });
    }
  }

  // Sort issues by severity
  issues.sort((a, b) => {
    const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
    return severityOrder[b.severity] - severityOrder[a.severity];
  });

  return {
    page_metadata: {
      title: head.title,
      url: head.url,
      description: head.meta.find(m => m.name === 'description' || m.name === 'Description')?.content,
      viewport: {
        width: viewport.width,
        height: viewport.height,
        device_type: getDeviceType()
      },
      timestamp: new Date().toISOString(),
      analysis_scope: analysisScope || 'viewport'
    },
    elements: allElements,
    issues_detected: issues.slice(0, 10), // Top 10 issues
    positive_notes: positiveNotes
  };
};

// ==========================================
// OPENAI PROMPT GENERATOR
// ==========================================

export const generateOpenAIPrompt = (atariData: AtariMatesAnalysis): string => {
  return `You are now acting as the AtariMates — Atarim's AI-powered specialist team members. The source code of the viewport of a webpage is provided in the JSON data below. A screenshot of the same viewport is also provided.

AtariMate Roles & Responsibilities:
• Pixel – Design & UI Consistency: Notices spacing issues, off-brand colors, alignment, inconsistent components
• Navi – UX & Accessibility: Detects unclear flows, bad UX patterns, poor accessibility (contrast, tab order, etc.)
• Index – SEO & Performance: Looks for slow load traps, missing SEO tags, images without alt text, structure issues
• Lexi – Content & Readability: Improves tone, clarity, label wording, CTAs, confusing microcopy
• Glitch – Functionality & Broken Links: Finds broken buttons, console errors, links that go nowhere, unresponsive elements

Page Information:
- Title: ${atariData.page_metadata.title}
- URL: ${atariData.page_metadata.url}
- Device: ${atariData.page_metadata.viewport.device_type} (${atariData.page_metadata.viewport.width}x${atariData.page_metadata.viewport.height})
- Elements analyzed: ${atariData.elements.length}

Pre-detected Issues for Reference:
${atariData.issues_detected.map((issue, i) =>
    `${i + 1}. ${issue.expert} (${issue.badge}): ${issue.description} [Element: ${issue.element.id}]`
  ).join('\n')}

JSON Data:
${JSON.stringify(atariData, null, 2)}

Output Requirements:
You must generate the Top 10 most important suggestions across all AtariMates combined. These must be:
- 1 specific point per suggestion, tied to a visible UI element
- Brief and clear — like a Slack message from a teammate
- Maintain a friendly feel, you are their mate
- Include 1 positive note in each review (out of the 10)

For each detected issue, return ONLY this JSON structure:

{
  "aiDesignReviewIssues": [
    {
      "expert": "Pixel|Navi|Index|Lexi|Glitch",
      "badge": "one word classification",
      "element": {
        "id": "node-{x}",
        "path": "exact path from JSON"
      },
      "issueType": "short classification",
      "description": "concise explanation"
    }
  ]
}

CRITICAL: Use only the exact "id" and "path" values from the JSON data provided. Do not make up IDs or paths.

Speak in each AtariMate's voice: Lexi is clear and human, Pixel is witty but detail-obsessed, Glitch is dry and reliable, Navi is helpful and inclusive, Index is performance-focused.

Return ONLY the JSON structure. No additional text or explanations.`;
};

// ==========================================
// USAGE EXAMPLE
// ==========================================

export default useViewportAnalyzer;

// ==========================================
// USAGE EXAMPLES
// ==========================================

/*
// Example 1: Analyze only visible viewport (default behavior)
const { analyzeViewport } = useViewportAnalyzer();
const viewportAnalysis = analyzeViewport();

// Example 2: Analyze full document
const { analyzeViewport } = useViewportAnalyzer();
const fullDocumentAnalysis = analyzeViewport({ analyzeFullDocument: true });

// Example 3: Convert to AtariMates format
const atariMatesData = convertToAtariMatesFormat(fullDocumentAnalysis);

// Example 4: Generate OpenAI prompt
const prompt = generateOpenAIPrompt(atariMatesData);
*/